import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Checkbox, Col, Row, Spin, Collapse, Breadcrumb, Select, Drawer } from "antd";
import {
  AppstoreOutlined,
  FilterOutlined,
  CloseOutlined,
  DownOutlined,
} from "@ant-design/icons";
import ResponsivePagination from "react-responsive-pagination";

import {
  getFilterListApi,
  getProductAllApi,
  getProductAllData,
  getFilterProductData2,
} from "../../Features/Product/Product";

import axios from "axios";

import styles from "./index.module.scss";
import { ProductCard } from "../../Component/productCard";
import { buildCategoryPath, parseCategoryPath, slugify } from "../../utils/categoryUrl";

import { BASE_URL } from "../../Constant";

// The "New Arrivals" menu entry lands on /product/new-arrivals. Kept as a
// constant so the link, the route check and the heading can never disagree.
// Kept below the imports: sitting between them tripped `import/first`, which
// react-app treats as an error, so the whole production build refused to compile.
const NEW_ARRIVALS_SLUG = "new-arrivals";
const NEW_ARRIVALS_COUNT = 30;

// Back from a product must land on the SAME page of the list (Adnan, 2026-09-27: page 2 → a product → Back
// showed page 1). The page, filters, sort, page size and scroll live in React state, which is lost when the
// product page replaces this one, so each history entry keeps its own copy under `location.key` — the key
// React Router hands back on Back/Forward and keeps across a reload. A fresh visit has a new key, so it still
// starts at page 1. sessionStorage can throw (private mode, quota); then Back simply starts at page 1 again.
// React Router keys a document's FIRST history entry "default" on every load, so that key alone would carry
// one visit's page into the next fresh visit of any list; tie it to this page load instead (Back within the
// visit still finds it, a new visit starts on page 1).
const DOC_ID = Math.random().toString(36).slice(2);
const listStateKey = (key) => `plp:${key === "default" ? `default-${DOC_ID}` : key}`;
const readListState = (key) => {
  try {
    const raw = sessionStorage.getItem(listStateKey(key));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeListState = (key, patch) => {
  try {
    sessionStorage.setItem(listStateKey(key), JSON.stringify({ ...(readListState(key) || {}), ...patch }));
  } catch {
    /* see above */
  }
};
const sameIds = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

// Menu-aware reconstruction of filter context for the *no nav-state* case
// (direct visit / refresh / shared link / home feature tile). The splat
// segments are positional and ambiguous: `/product/cartier` could mean a
// brand ("Cartier") or a Product Range L1 category. Clicking a menu item
// passes location.state so the page knows; a cold load does not.
//
// To disambiguate we look segment 1 up against the real site menus:
//   - branded_jewellery brand label -> brand context (menuGroup branded_jewellery)
//   - watches brand label           -> brand context (menuGroup watches)
//   - product_range L1 label        -> category context (menuGroup product_range)
// Matching is done with the shared slugify() so a URL slug like
// "van-cleef-and-arpels" matches the label "Van Cleef & Arpels".

// Pull a menu array from its localStorage cache; null if missing/empty.
const readCachedMenu = (lsKey) => {
  try {
    const raw = localStorage.getItem(lsKey);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

// Obtain all three menus: prefer the Header's localStorage caches, fetch any
// missing one from the /menu/... endpoint (same source the Header uses).
const loadMenus = async () => {
  let branded = readCachedMenu("tdc_branded_jewellery_menu");
  let productRange = readCachedMenu("tdc_product_range_menu");
  let watches = readCachedMenu("tdc_watches_menu");

  const fetchMenu = async (group) => {
    try {
      const res = await axios.get(`${BASE_URL}/menu/${group}`);
      const arr = res?.data?.data;
      return Array.isArray(arr) && arr.length > 0 ? arr : null;
    } catch {
      return null;
    }
  };

  const [b, p, w] = await Promise.all([
    branded ? Promise.resolve(branded) : fetchMenu("branded_jewellery"),
    productRange ? Promise.resolve(productRange) : fetchMenu("product_range"),
    watches ? Promise.resolve(watches) : fetchMenu("watches"),
  ]);
  return { branded: b, productRange: p, watches: w };
};

// Find a top-level menu item whose label slugifies to `slug`.
const findBySlug = (menu, slug) =>
  Array.isArray(menu)
    ? menu.find((it) => slugify(it?.label) === slug)
    : undefined;

// Given de-slugified path segments and the three menus, build the correct
// filter-context state. Returns null when no menu match is found so the
// caller can fall back to the existing positional reconstruction.
const resolveStateFromMenus = (segments, menus) => {
  if (!segments.length) return null;
  const seg1Slug = slugify(segments[0]);

  // segment 1 is a branded_jewellery brand
  const brandedBrand = findBySlug(menus.branded, seg1Slug);
  if (brandedBrand) {
    return {
      brandName: brandedBrand.label,
      ...(segments[1] ? { menuLevel1: segments[1] } : {}),
      ...(segments[2] ? { menuLevel2: segments[2] } : {}),
      menuGroup: "branded_jewellery",
      item: segments[segments.length - 1],
    };
  }

  // segment 1 is a watches brand
  const watchesBrand = findBySlug(menus.watches, seg1Slug);
  if (watchesBrand) {
    return {
      brandName: watchesBrand.label,
      ...(segments[1] ? { menuLevel1: segments[1] } : {}),
      ...(segments[2] ? { menuLevel2: segments[2] } : {}),
      menuGroup: "watches",
      item: segments[segments.length - 1],
    };
  }

  // segment 1 is a product_range level-1 category
  const prCategory = findBySlug(menus.productRange, seg1Slug);
  if (prCategory) {
    return {
      menuLevel1: prCategory.label,
      ...(segments[1] ? { menuLevel2: segments[1] } : {}),
      ...(segments[2] ? { menuLevel3: segments[2] } : {}),
      menuGroup: "product_range",
      item: segments[segments.length - 1],
    };
  }

  return null;
};

const capitalize = (str) =>
  str
    ? str
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    : "";

function Catagory() {
  const { Panel } = Collapse;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // The category route is `/product/*` — the splat encodes the full menu
  // hierarchy (brand + menuLevel1/2/3) as path segments.
  const { "*": splat } = useParams();
  const { state: rawState } = location;
  // /product/all is the "shop everything" entry — no menu filter so the
  // API returns the full catalog. Anything else, when no nav state is
  // present (direct visit / refresh / shared link), is reconstructed from
  // the URL segments mapped positionally onto menuLevel1/2/3.
  const pathSegments = parseCategoryPath(splat);
  const isShopAll = pathSegments.length === 0;
  // "New Arrivals" is a view, not a category. Nothing in the catalogue is
  // tagged as new — it is simply the most recently added pieces, whatever they
  // happen to be — so it filters on nothing and takes the same path as
  // /product/all with the order and the count fixed. Derived from the URL
  // rather than nav state so a refresh or a shared link behaves the same.
  const isNewArrivals =
    pathSegments.length === 1 && slugify(pathSegments[0]) === NEW_ARRIVALS_SLUG;
  // Positional reconstruction — used directly when there IS nav state isn't
  // needed, and as the fallback when a cold load's segment 1 matches no menu.
  const positionalState = isShopAll
    ? { item: "All Products", shopAll: true }
    : isNewArrivals
    ? { item: "New Arrivals", shopAll: true, newArrivals: true }
    : {
        ...(pathSegments[0] ? { menuLevel1: pathSegments[0] } : {}),
        ...(pathSegments[1] ? { menuLevel2: pathSegments[1] } : {}),
        ...(pathSegments[2] ? { menuLevel3: pathSegments[2] } : {}),
        item: pathSegments[pathSegments.length - 1],
      };

  // Cold load (no nav state) of a real category path needs the menus to
  // disambiguate brand vs category. `menuState` holds the resolved context
  // once loadMenus() reports back; until then `menuResolved` is false and we
  // hold the spinner so the wrong (positional) filter never hits the API.
  const needsMenuResolution = !rawState && !isShopAll && !isNewArrivals;
  const [menuState, setMenuState] = useState(null);
  const [menuResolved, setMenuResolved] = useState(!needsMenuResolution);

  // In-app navigation: location.state is authoritative — never touched.
  // Cold load: use the menu-resolved state once ready, else fall back to
  // positional. Shop-all stays the special "shop everything" entry.
  const state = rawState
    ? rawState
    : isShopAll
      ? positionalState
      : menuState || positionalState;
  const [data, setData] = useState();

  // Track which location.key's fetch result is currently in Redux so we
  // can hide stale data from previous category visits. Without this, two
  // flash scenarios occur:
  //   1. Home → Cartier: Catagory mounts fresh, but productAllData still
  //      holds the previous category's results from earlier in the session.
  //   2. Catagory → Catagory: same component stays mounted while URL
  //      changes, so the first render after navigation paints the old
  //      products before useEffect can dispatch the clear.
  // Spinner shows whenever loadedKey !== location.key; only flips to data
  // once the fetch tagged with the current key reports back.
  const [loadedKey, setLoadedKey] = useState(null);
  // Always holds the latest location.key so a fetch that resolves late can
  // tell whether the user is still on the page that requested it.
  const locationKeyRef = useRef(location.key);
  locationKeyRef.current = location.key;

  // What this history entry showed when the customer left it ({} on a first visit), read once on mount.
  const [restored] = useState(() => readListState(location.key) || {});
  const [brandName, setBrandName] = useState(restored.brandName || []);
  const [colorsData, setColorsData] = useState(restored.colorsData || []);
  const [categoryData, setcategoryData] = useState(restored.categoryData || []);
  const [gridCols, setGridCols] = useState(restored.gridCols || 4);
  const [itemsPerPage, setItemsPerPage] = useState(restored.itemsPerPage || 20);
  const [sortBy, setSortBy] = useState(restored.sortBy || "price_asc");
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // An empty result means two completely different things, and the customer is
  // owed the right one. These are the only three the shopper can set from this
  // page; sort cannot empty a list, and paging is guarded by the API's own
  // total. So: any of them set → their filters are too narrow. None set → the
  // category itself has nothing in it yet.
  const hasActiveFilters =
    (categoryData?.length || 0) + (brandName?.length || 0) + (colorsData?.length || 0) > 0;

  // The deepest level the customer actually navigated to, named the way the
  // breadcrumb names it, so the message is about the page they are on rather
  // than a generic apology. Shop All and New Arrivals are not categories and get
  // no name — an empty "All Products" is a system fault, not a gap in a range.
  const categoryLabel = (() => {
    if (state?.shopAll) return "";
    const leaf = state?.menuLevel3 || state?.menuLevel2 || state?.menuLevel1;
    return leaf ? capitalize(leaf) : "";
  })();


  const [currentPage, setCurrentPage] = useState(restored.page || 1);
  const userid = useSelector((state) => state.user?.userId);
  const loader = useSelector((state) => state.product.productLoading);

  const filterSideData = useSelector((state) => state.product?.filterList);
  const reduxProducts = useSelector((state) => state.product?.productAllData);
  const reduxPagination = useSelector((state) => state.product?.filterProduct2);
  // Hard gate: never expose Redux product/pagination data to render unless
  // we have proof a fetch completed for the *current* location.key. Stops
  // any flash of products from a previous category visit before the
  // upstream dispatches have cleared Redux on this URL change.
  const filterProducct = loadedKey === location.key ? reduxProducts : null;
  const paginationData = loadedKey === location.key ? reduxPagination : null;

  // Nothing to show, nothing filtered out, and the data on screen belongs to
  // THIS url — the guard matters, or the toolbar blanks for a moment mid-navigation.
  const isEmptyCategory =
    !loader &&
    loadedKey === location.key &&
    Array.isArray(filterProducct) &&
    filterProducct.length === 0 &&
    !hasActiveFilters;

  // Where the list really ends, and where the pager stops offering pages — the
  // pager has always capped at 10. "Last page" is measured against the REAL
  // total: on a list long enough to be truncated the customer has NOT seen
  // everything, and the note below must not tell them they have.
  const totalPages = paginationData?.totalPages || 1;
  const pagerPages = Math.min(totalPages, 10);
  const isLastPage = currentPage >= totalPages;

  // On every URL change: clear stale products + count so the UI shows a
  // proper loading state (not the previous category's items + "0 Products"
  // header) while the new fetch is in flight. Functional setState returns
  // the same reference when nothing changes so React doesn't re-render twice
  // and the fetch effect below only fires once.
  // Page and filters come back from this history entry's saved copy (Back
  // from a product), else reset to page 1 unfiltered; sort, page size and
  // grid carry over from the previous category unless the entry saved its own.
  useEffect(() => {
    const saved = readListState(location.key) || {};
    const page = saved.page || 1;
    const restore = (setter, list = []) => setter(prev => (sameIds(prev, list) ? prev : list));
    setData(state);
    restore(setcategoryData, saved.categoryData);
    restore(setBrandName, saved.brandName);
    restore(setColorsData, saved.colorsData);
    setCurrentPage(prev => (prev !== page ? page : prev));
    if (saved.sortBy) setSortBy(saved.sortBy);
    if (saved.itemsPerPage) setItemsPerPage(saved.itemsPerPage);
    if (saved.gridCols) setGridCols(saved.gridCols);
    // Record what this entry will show straight away — the save effect below
    // skips this render, whose state still belongs to the previous URL.
    writeListState(location.key, {
      page,
      categoryData: saved.categoryData || [],
      brandName: saved.brandName || [],
      colorsData: saved.colorsData || [],
      sortBy: saved.sortBy || sortBy,
      itemsPerPage: saved.itemsPerPage || itemsPerPage,
      gridCols: saved.gridCols || gridCols,
    });
    dispatch(getProductAllData(null));
    dispatch(getFilterProductData2(null));
    dispatch(getFilterListApi());
    window.scrollTo(0, 0);
  }, [location.key]); // eslint-disable-line

  // Keep this entry's copy current as the customer pages, filters and sorts.
  const savedKeyRef = useRef(location.key);
  useEffect(() => {
    if (savedKeyRef.current !== location.key) {
      savedKeyRef.current = location.key;
      return;
    }
    writeListState(location.key, { page: currentPage, categoryData, brandName, colorsData, sortBy, itemsPerPage, gridCols });
  }, [location.key, currentPage, categoryData, brandName, colorsData, sortBy, itemsPerPage, gridCols]);

  // Back from a product: once this entry's products are on screen again,
  // return to where the customer clicked (saved by the grid's click capture),
  // but only on the page that scroll belongs to.
  const scrollRestoredRef = useRef(null);
  useEffect(() => {
    if (loadedKey !== location.key || scrollRestoredRef.current === location.key) return;
    scrollRestoredRef.current = location.key;
    const saved = readListState(location.key);
    if (saved?.scrollY > 0 && saved.scrollPage === currentPage) {
      requestAnimationFrame(() => window.scrollTo(0, saved.scrollY));
    }
  }, [loadedKey, location.key]); // eslint-disable-line

  // Cold-load only: resolve the splat segments against the site menus so we
  // know whether segment 1 is a brand or a category before filtering. Runs
  // per URL change; resets the gate first so the spinner shows (rather than
  // flashing a positional-filtered result) while the menus load. In-app
  // navigation (rawState present) and /product/all skip this entirely.
  useEffect(() => {
    if (!needsMenuResolution) {
      setMenuResolved(true);
      setMenuState(null);
      return;
    }
    let cancelled = false;
    setMenuResolved(false);
    setMenuState(null);
    loadMenus().then((menus) => {
      if (cancelled) return;
      setMenuState(resolveStateFromMenus(pathSegments, menus));
      setMenuResolved(true);
    });
    return () => { cancelled = true; };
  }, [location.key]); // eslint-disable-line

  // Single product fetch — fires when URL changes (via location.key) OR
  // when the user toggles a filter. React 18 batches the resets above
  // with the location.key change, so each nav results in exactly ONE
  // /product/get call rather than the previous two.
  useEffect(() => {
    // Hold the fetch until the menus have resolved on a cold load — firing
    // with the positional fallback would re-trigger once menuState lands and
    // could briefly query the wrong menu field.
    if (!menuResolved) return;
    const params = {
      page: currentPage,
      limit: isNewArrivals ? NEW_ARRIVALS_COUNT : itemsPerPage,
      category: categoryData?.join(","),
      brand: brandName?.join(","),
      color: colorsData?.join(","),
      userId: userid,
      // The whole point of the view is the order, so the sort control does not
      // get to change it — it is hidden here rather than left to mislead.
      sort: isNewArrivals ? "newest" : sortBy,
    };
    if (state?.menuLevel1) params.menuLevel1 = state.menuLevel1;
    if (state?.menuLevel2) params.menuLevel2 = state.menuLevel2;
    if (state?.menuLevel3) params.menuLevel3 = state.menuLevel3;
    if (state?.menuGroup) params.menuGroup = state.menuGroup;
    if (state?.brandName) params.brandName = state.brandName;
    const fetchKey = location.key;
    dispatch(getProductAllApi(params, () => {
      // Ignore a fetch that resolved after the user navigated away — keeps
      // the previous page's products from flashing on the new one.
      if (locationKeyRef.current === fetchKey) setLoadedKey(fetchKey);
    }));
  }, [categoryData, brandName, colorsData, currentPage, userid, itemsPerPage, sortBy, location.key, menuResolved]); // eslint-disable-line

  const onChange = (e, item, filterType) => {
    setCurrentPage(1);

    if (filterType === "color") {
      setColorsData((prev) =>
        e.target.checked
          ? [...prev, item]
          : prev.filter((color) => color !== item),
      );
    }

    if (filterType === "brands") {
      setBrandName((prev) =>
        e.target.checked
          ? [...prev, item._id]
          : prev.filter((id) => id !== item._id),
      );
    }

    if (filterType === "category") {
      setcategoryData((prev) =>
        e.target.checked
          ? [...prev, item._id]
          : prev.filter((id) => id !== item._id),
      );
    }
  };

  const handleClear = () => {
    setBrandName([]);
    setColorsData([]);
    setcategoryData([]);
    setCurrentPage(1);
  };

  const onShowSizeChange = (current) => {
    setCurrentPage(current);
    window.scrollTo(0, 0);
  };

  const activeFilterCount =
    categoryData.length + brandName.length + colorsData.length;

  const gridColMap = {
    2: { xs: 12, md: 12, lg: 12, xl: 12 },
    3: { xs: 12, md: 8, lg: 8, xl: 8 },
    4: { xs: 12, md: 8, lg: 6, xl: 6 },
    5: { xs: 12, md: 8, lg: 6, xl: 4, style: { maxWidth: "20%", flex: "0 0 20%" } },
  };

  const filterContent = (
    <Collapse defaultActiveKey={["1"]} expandIconPosition="end" ghost>
      <Panel header="Categories" key="1">
        <div className={styles.checkList}>
          {filterSideData?.categories?.map((item, index) => (
            <Checkbox
              onChange={(e) => onChange(e, item, "category")}
              key={index}
              className={styles.checkItem}
              checked={categoryData.includes(item._id)}
            >
              {capitalize(item.Category_Name)}
            </Checkbox>
          ))}
        </div>
      </Panel>

      <Panel header="Brands & Collections" key="2">
        <div className={styles.checkList}>
          {filterSideData?.brand?.map((item, index) => (
            <Checkbox
              onChange={(e) => onChange(e, item, "brands")}
              key={index}
              className={styles.checkItem}
              checked={brandName.includes(item._id)}
            >
              {capitalize(item.Data_Name)}
            </Checkbox>
          ))}
        </div>
      </Panel>

      <Panel header="Colours" key="3">
        <div className={styles.checkList}>
          {filterSideData?.colors?.map((item, index) => (
            <Checkbox
              onChange={(e) => onChange(e, item, "color")}
              key={index}
              className={styles.checkItem}
              checked={colorsData.includes(item)}
            >
              {capitalize(item)}
            </Checkbox>
          ))}
        </div>
      </Panel>
    </Collapse>
  );

  return (
    <div className={styles.shopPage}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbBar}>
        <div className={styles.shopContainer}>
          <Breadcrumb
            className={styles.breadcrumb}
            items={[
              { title: <Link to="/">Home</Link> },
              ...(state?.brandName && state?.menuLevel1
                ? [
                    { title: <Link to={buildCategoryPath({ brandName: state.brandName })} state={{ brandName: state.brandName, menuGroup: state.menuGroup }}>{capitalize(state.brandName)}</Link> },
                    { title: capitalize(state.menuLevel1) },
                    ...(state?.menuLevel2 ? [{ title: capitalize(state.menuLevel2) }] : []),
                    ...(state?.menuLevel3 ? [{ title: capitalize(state.menuLevel3) }] : []),
                  ]
                : state?.menuLevel3 && state?.menuLevel2 && state?.menuLevel1
                ? [
                    { title: <Link to={buildCategoryPath({ menuLevel1: state.menuLevel1 })} state={{ menuLevel1: state.menuLevel1, menuGroup: state.menuGroup }}>{capitalize(state.menuLevel1)}</Link> },
                    { title: <Link to={buildCategoryPath({ menuLevel1: state.menuLevel1, menuLevel2: state.menuLevel2 })} state={{ menuLevel1: state.menuLevel1, menuLevel2: state.menuLevel2, menuGroup: state.menuGroup }}>{capitalize(state.menuLevel2)}</Link> },
                    { title: capitalize(state.menuLevel3) },
                  ]
                : state?.menuLevel2 && state?.menuLevel1
                ? [
                    { title: <Link to={buildCategoryPath({ menuLevel1: state.menuLevel1 })} state={{ menuLevel1: state.menuLevel1, menuGroup: state.menuGroup }}>{capitalize(state.menuLevel1)}</Link> },
                    { title: capitalize(state.menuLevel2) },
                  ]
                : [{ title: capitalize(state?.brandName || state?.menuLevel1 || data?.item) || "Shop" }]),
            ]}
          />
        </div>
      </div>

      <div className={styles.shopContainer}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <span className={styles.resultCount}>
              {loader || loadedKey !== location.key
                ? "Loading…"
                : /* "0 Products" over an empty, unfiltered category says the same
                     unhelpful thing the panel below used to, and reads as a fault
                     rather than a range still being built. The panel does the
                     talking there; a count of nothing adds nothing. With filters
                     on it stays, because then it IS the answer to what the
                     customer just did. */
                  isEmptyCategory
                ? ""
                : `${paginationData?.totalProducts || filterProducct?.length || 0} Products`}
            </span>
          </div>

          <div className={styles.toolbarRight}>
            <div className={styles.gridToggle}>
              {[5, 4, 3, 2].map((cols) => (
                <button
                  key={cols}
                  className={`${styles.gridBtn} ${gridCols === cols ? styles.gridBtnActive : ""}`}
                  onClick={() => setGridCols(cols)}
                  title={`${cols} per row`}
                >
                  <AppstoreOutlined />
                  <span>{cols}</span>
                </button>
              ))}
            </div>

            {/* New Arrivals IS the newest-first order — a sort control there
                would either do nothing or quietly turn it into a different
                list, so it is not offered. */}
            {!isNewArrivals && (
              <Select
                value={sortBy}
                onChange={(val) => {
                  setSortBy(val);
                  setCurrentPage(1);
                }}
                className={styles.perPageSelect}
                options={[
                  { value: "price_asc", label: "Price: Low to High" },
                  { value: "price_desc", label: "Price: High to Low" },
                  { value: "newest", label: "Newest First" },
                ]}
              />
            )}

            <Select
              value={itemsPerPage}
              onChange={(val) => {
                setItemsPerPage(val);
                setCurrentPage(1);
              }}
              className={styles.perPageSelect}
              options={[
                { value: 20, label: "20 per page" },
                { value: 40, label: "40 per page" },
                { value: 60, label: "60 per page" },
                { value: 100, label: "100 per page" },
              ]}
            />
          </div>
        </div>

        {/* Product Grid — show spinner whenever the displayed data doesn't
            match the current URL (loadedKey gate). This catches both the
            home→category mount with leftover Redux data AND the
            category→category nav before the clear-dispatch lands. */}
        {(loader || loadedKey !== location.key || filterProducct == null) ? (
          <div className={styles.loaderWrap}>
            <Spin size="large" />
          </div>
        ) : filterProducct?.length > 0 ? (
          <Row
            gutter={[16, 24]}
            onClickCapture={() => writeListState(location.key, { scrollY: window.scrollY, scrollPage: currentPage })}
          >
            {filterProducct?.map((item, index) => (
              <Col key={item?._id || index} {...gridColMap[gridCols]}>
                <ProductCard item={item} />
              </Col>
            ))}
          </Row>
        ) : hasActiveFilters ? (
          /* Filters are on, so the shelf is not necessarily empty — these
             filters are. Telling someone a category is "coming soon" when they
             have simply narrowed it too far would be wrong and would send them
             away from stock we do have. */
          <div className={styles.emptyState}>
            <p>Nothing matches those filters</p>
            <span>Clear one and try again.</span>
          </div>
        ) : (
          /* Nothing filtered, and nothing here: the category is genuinely empty.
             The menu deliberately carries subcategories we have not stocked yet
             (Bracelets keeps four, Earrings two), so a customer WILL land on one
             — and "0 products found" reads like a fault on our side rather than
             a collection still being built. Say so, and give them somewhere to
             go: everything we make is made to order, so an empty shelf is not
             the end of the conversation. */
          <div className={styles.emptyState}>
            <p>{categoryLabel ? `${categoryLabel} — coming soon` : "Coming soon"}</p>
            <span>
              We are still building this part of the collection. Every piece we make is made to
              order, so if you have something in mind for a customer we can quote it now.
            </span>
            <div className={styles.emptyActions}>
              <Link to="/product/all" className={styles.emptyPrimary}>Browse everything</Link>
              <Link to="/design-and-cad" className={styles.emptySecondary}>Have it made</Link>
            </div>
          </div>
        )}

        {/* Pagination */}
        {filterProducct?.length > 0 && (
          <div className={styles.paginationWrap}>
            <ResponsivePagination
              current={currentPage}
              total={pagerPages}
              onPageChange={onShowSizeChange}
              pageLinkClassName={styles.paginationPage}
              activeItemClassName={styles.activePage}
            />
          </div>
        )}

        {/* The range is deliberately still growing, and a grid that simply stops
            reads as "that is all there is". On the LAST page the customer has
            seen everything this list holds, so saying more is on the way is both
            true and useful; on an earlier page it would compete with the next
            page of products that already exist. */}
        {filterProducct?.length > 0 && isLastPage && (
          <div className={styles.moreSoon}>
            <span className={styles.moreSoonMark} aria-hidden="true">◆</span>
            <p className={styles.moreSoonTitle}>More pieces coming soon</p>
            <span className={styles.moreSoonBody}>
              We are adding new designs to this collection all the time. Everything we make is
              made to order, so if you have something specific in mind for a customer we can
              quote it now.
            </span>
            <Link to="/design-and-cad" className={styles.moreSoonLink}>
              Have it made
            </Link>
          </div>
        )}
      </div>

    </div>
  );
}

export default Catagory;
